import type { FundSymbol } from '../products'

import {
  type fund_prices_type,
  useGetFundPrices,
  useGetFundTotalTokens,
  useGetMinterLimitLeft,
  useGetMinterPaused,
  useGetMinterPriceSafety,
} from './funds_block_queries'

export function useGetFundTVL({ symbol }: { symbol: FundSymbol }) {
  const prices_query = useGetFundPrices({ symbol })

  const { data: total_tokens, isLoading: t_isLoading } = useGetFundTotalTokens({
    symbol,
  })

  return {
    tvl: (total_tokens ?? 0) * (prices_query.data?.dex ?? 0),
    isLoading: prices_query.isLoading || t_isLoading,
  }
}

// eslint-disable-next-line complexity
export function check_disabled({
  paused,
  safety,
  prices,
  limit,
  type,
}: {
  paused?: boolean
  safety?: { price_safety_percentage: number }
  prices?: fund_prices_type
  limit?: number
  type: 'mint' | 'redeem'
}): {
  isSuccess: boolean
  disabled: boolean | 'temporary'
  reason: string
} {
  if (paused == null || safety == null || prices == null || limit == null) {
    return {
      isSuccess: false,
      disabled: paused == true,
      reason: '',
    }
  }

  if (paused) {
    return {
      isSuccess: true,
      disabled: true,
      reason: `Contract paused`,
    }
  }

  if (prices.oracle_outdated) {
    return {
      isSuccess: true,
      disabled: true,
      reason: 'NAV oracle outdated',
    }
  }

  const inside_safety =
    (Math.abs(prices.dex - prices.observation) / prices.observation) * 100 <=
    safety.price_safety_percentage

  if (!inside_safety) {
    return {
      isSuccess: true,
      disabled: 'temporary',
      reason: `Price outside safety range`,
    }
  }

  const is_limit_left = limit > 0

  if (!is_limit_left && type == 'mint') {
    return {
      isSuccess: true,
      disabled: true,
      reason: 'Out of deposit capacity',
    }
  }

  return {
    isSuccess: true,
    disabled: false,
    reason: '',
  }
}

// eslint-disable-next-line complexity
export function useGetMinterDisabled({ symbol }: { symbol: FundSymbol }) {
  const { data: paused } = useGetMinterPaused({ symbol })
  const { data: safety } = useGetMinterPriceSafety({ symbol })
  const { data: prices } = useGetFundPrices({ symbol })
  const { data: limit } = useGetMinterLimitLeft({ symbol })

  return check_disabled({ paused, safety, prices, limit, type: 'mint' })
}
